/**
 * Rend une ligne d'ordonnance en langage naturel.
 *
 * « Insuline, tous les jours à 08:00 et 19:00, du 01/03 au 31/05 »
 *
 * C'est ce que lit l'infirmière sur la fiche patient : pas `QUOTIDIEN` +
 * `["08:00","19:00"]`, mais une phrase. Le rendu est testé cas par cas, parce
 * que c'est exactement le genre de fonction où une virgule mal placée passe
 * inaperçue en revue et saute aux yeux en démonstration.
 */

import type { Frequence, LigneOrdonnance } from '../api/types'
import { enumerer, formatDate, formatHeure } from './index'
import { JOURS_SEMAINE } from '../temps'

/**
 * "0,2,4" → ["lundi", "mercredi", "vendredi"].
 *
 * **0 = lundi** (§5.8 du contrat). `getDay()` de JavaScript renvoie 0 pour
 * dimanche : le réutiliser ici décalerait tous les libellés d'un jour.
 */
export function joursDepuisChaine(jours: string | null): string[] {
  if (!jours) return []
  return jours
    .split(',')
    .map((n) => Number(n.trim()))
    .filter((n) => Number.isInteger(n) && n >= 0 && n <= 6)
    .sort((a, b) => a - b)
    .map((n) => JOURS_SEMAINE[n] ?? '')
    .filter(Boolean)
}

/** Le rythme seul : « tous les jours à 08:00 et 19:00 ». */
export function decrireRythme(
  frequence: Frequence,
  joursSemaine: string | null,
  intervalleJours: number | null,
  horaires: readonly string[],
): string {
  const heures = horaires.map(formatHeure)
  const aHeures = heures.length > 0 ? ` à ${enumerer(heures)}` : ''

  switch (frequence) {
    case 'QUOTIDIEN':
      return `tous les jours${aHeures}`

    case 'JOURS_SEMAINE': {
      const jours = joursDepuisChaine(joursSemaine)
      if (jours.length === 0) return `certains jours${aHeures}`
      if (jours.length === 7) return `tous les jours${aHeures}`
      return `les ${enumerer(jours)}${aHeures}`
    }

    case 'INTERVALLE_JOURS': {
      if (!intervalleJours || intervalleJours < 1) return `à intervalle non précisé${aHeures}`
      if (intervalleJours === 1) return `tous les jours${aHeures}`
      if (intervalleJours === 2) return `un jour sur deux${aHeures}`
      return `tous les ${intervalleJours} jours${aHeures}`
    }

    default:
      return `rythme non précisé${aHeures}`
  }
}

/**
 * La phrase complète, avec le soin et la période.
 *
 * `libelleSoin` vient du type de soin ; la période, de l'ordonnance. Les deux
 * sont passés en paramètre plutôt que résolus ici : cette fonction reste pure
 * et donc testable sans monter le moindre contexte.
 */
export function decrireLigneOrdonnance(
  ligne: Pick<
    LigneOrdonnance,
    'frequence' | 'jours_semaine' | 'intervalle_jours' | 'horaires'
  >,
  libelleSoin: string,
  dateDebut?: string,
  dateFin?: string,
): string {
  const rythme = decrireRythme(
    ligne.frequence,
    ligne.jours_semaine,
    ligne.intervalle_jours,
    ligne.horaires,
  )
  const periode =
    dateDebut && dateFin ? `, du ${formatDate(dateDebut)} au ${formatDate(dateFin)}` : ''
  return `${libelleSoin}, ${rythme}${periode}`
}
